import { useState } from 'react';
import { useTenants } from '../../../../components/tenant-selector/useTenants';
import { useSegmentDefinitions } from '../../edit-automation-template/useSegmentDefinitions';
import { StatusFilter, type SortDirection } from '../../engage-automations-tab/templates-manager/types';
import {
  deriveSegmentStatus,
  formatSegmentName,
  resolveSyncedBusinessIds,
  type SegmentListStatus,
} from './segmentStatus';
import type { SegmentListRow, SegmentListView, SegmentSortKey } from './types';

const STATUS_SORT_RANK: Record<SegmentListStatus, number> = {
  [StatusFilter.Draft]: 0,
  [StatusFilter.Active]: 1,
};

const compareRows = (
  a: SegmentListRow,
  b: SegmentListRow,
  sortKey: SegmentSortKey,
  sortDirection: SortDirection,
): number => {
  let primary = 0;
  if (sortKey === 'name') {
    primary = a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
  } else if (sortKey === 'tenants') {
    primary = a.syncedTenantCount - b.syncedTenantCount;
  } else if (sortKey === 'status') {
    primary = STATUS_SORT_RANK[a.status] - STATUS_SORT_RANK[b.status];
  } else {
    primary = (a.updatedAt ? Date.parse(a.updatedAt) : 0) - (b.updatedAt ? Date.parse(b.updatedAt) : 0);
  }

  const comparison =
    primary === 0 ? a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()) : primary;
  return sortDirection === 'asc' ? comparison : comparison * -1;
};

/**
 * Client-side search / status (Draft/Active) / tenant filter / sort for the partner segments list.
 * Fetches the segments and tenant roster itself; status derives from the raw `targetedTenantsFilter`
 * and "tenants" is the synced-tenant count, resolved against the live roster.
 */
export function useSegmentListView(): SegmentListView {
  const { data: segmentsData, isLoading, error } = useSegmentDefinitions();
  const { data: tenants } = useTenants();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.All);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SegmentSortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const segments = segmentsData ?? [];
  const roster = tenants ?? [];
  const rosterBusinessIds = roster.map(tenant => tenant.id);

  const allRows: SegmentListRow[] = segments.map(segment => {
    const syncedBusinessIds = resolveSyncedBusinessIds(segment, rosterBusinessIds);
    return {
      segment,
      displayName: formatSegmentName(segment.name),
      description: segment.description || '',
      status: deriveSegmentStatus(segment),
      syncedTenantCount: syncedBusinessIds.length,
      syncedBusinessIds,
      updatedAt: segment.updatedAt,
    };
  });

  const statusCounts: Record<StatusFilter, number> = {
    [StatusFilter.All]: allRows.length,
    [StatusFilter.Draft]: 0,
    [StatusFilter.Available]: 0,
    [StatusFilter.Active]: 0,
  };
  for (const row of allRows) {
    statusCounts[row.status] += 1;
  }

  const syncedSegmentCountByBusinessId = new Map<string, number>();
  for (const row of allRows) {
    for (const businessId of row.syncedBusinessIds) {
      syncedSegmentCountByBusinessId.set(
        businessId,
        (syncedSegmentCountByBusinessId.get(businessId) ?? 0) + 1,
      );
    }
  }

  const tenantFilterOptions = roster
    .map(tenant => ({
      id: tenant.id,
      name: tenant.name,
      templateCount: syncedSegmentCountByBusinessId.get(tenant.id) ?? 0,
    }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const normalizedSearch = search.trim().toLowerCase();
  const selectedTenantIdSet = new Set(selectedTenantIds);

  const filteredRows = allRows.filter(row => {
    const matchesSearch =
      normalizedSearch.length === 0 || row.displayName.toLowerCase().includes(normalizedSearch);
    const matchesStatus = statusFilter === StatusFilter.All || row.status === statusFilter;
    const matchesTenant =
      selectedTenantIds.length === 0 || row.syncedBusinessIds.some(id => selectedTenantIdSet.has(id));
    return matchesSearch && matchesStatus && matchesTenant;
  });

  const rows = [...filteredRows].sort((a, b) => compareRows(a, b, sortKey, sortDirection));

  const toggleSort = (key: SegmentSortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
      return;
    }
    setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
  };

  const toggleTenant = (tenantId: string) =>
    setSelectedTenantIds(current =>
      current.includes(tenantId) ? current.filter(id => id !== tenantId) : [...current, tenantId],
    );

  const clearTenants = () => setSelectedTenantIds([]);

  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter(StatusFilter.All);
    setSelectedTenantIds([]);
  };

  const hasActiveFilters = statusFilter !== StatusFilter.All || selectedTenantIds.length > 0;

  return {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    statusCounts,
    selectedTenantIds,
    toggleTenant,
    clearTenants,
    tenantFilterOptions,
    sortKey,
    sortDirection,
    toggleSort,
    rows,
    totalCount: allRows.length,
    filteredCount: filteredRows.length,
    hasActiveFilters,
    clearAllFilters,
    isLoading,
    error,
  };
}
